import { useCallback, useEffect, useRef, useState } from 'react';
import { getNotificationSummary } from 'api/notifications';

const POLL_MS = 60_000;

/**
 * Global app notifications (header bell).
 * Polls for bot escalations, onboarding queues, etc.
 */
export default function useAppNotifications({ enabled = true } = {}) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  const refresh = useCallback(async () => {
    if (!enabled) return null;
    try {
      const data = await getNotificationSummary();
      if (mountedRef.current) {
        setSummary(data);
        setError(null);
      }
      return data;
    } catch (err) {
      if (mountedRef.current) {
        setError(err?.response?.data?.message || 'Failed to load notifications');
      }
      return null;
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    mountedRef.current = true;
    if (!enabled) return undefined;

    refresh();
    const timer = setInterval(refresh, POLL_MS);

    const onExternalRefresh = () => refresh();
    window.addEventListener('phnx-notifications-refresh', onExternalRefresh);

    return () => {
      mountedRef.current = false;
      clearInterval(timer);
      window.removeEventListener('phnx-notifications-refresh', onExternalRefresh);
    };
  }, [enabled, refresh]);

  return {
    summary,
    loading,
    error,
    refresh,
    totalUnread: summary?.total_unread ?? 0,
    items: summary?.items ?? []
  };
}
